/**
 * Admin wizards page for /admin/wizards route.
 * Lists all server-side wizard templates using KiiList.
 */

import { useState, useCallback, useMemo } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { KiiDialogLayout, KiiList, type KiiListColumn, type FilterConfig, type FilterValues, type SortState } from "../components/kii";
import { TableRow, TableCell } from "../components/ui/table";
import { useProfile } from "../contexts/ProfileContext";
import { listWizardTemplates, createWizardTemplate, type WizardTemplate } from "../services/naraDoor";
import { adminRoutes, publicRoutes } from "../lib/routes";
import { Wand2, Plus, Loader2, HelpCircle } from "lucide-react";

const columns: KiiListColumn[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "description", label: "Description" },
  { key: "entries", label: "Total Entries", sortable: true },
  { key: "created_at", label: "Start", sortable: true },
  { key: "closed_at", label: "End", sortable: true },
];

const statusFilterOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "closed", label: "Closed" },
];

const filters: FilterConfig[] = [
  {
    id: "status",
    label: "Status",
    placeholder: "All statuses",
    colorVariant: "primary",
    multiple: true,
    options: statusFilterOptions,
  },
];

function getStatus(tmpl: WizardTemplate): string {
  if (tmpl.closed_at) return "closed";
  return tmpl.is_active ? "active" : "inactive";
}

function sortTemplates(data: WizardTemplate[], sortState: SortState): WizardTemplate[] {
  if (!sortState.column || !sortState.direction) return data;
  const sorted = [...data];
  const dir = sortState.direction === "asc" ? 1 : -1;
  sorted.sort((a, b) => {
    switch (sortState.column) {
      case "name":
        return dir * a.name.localeCompare(b.name);
      case "entries":
        return dir * ((a.instance_count ?? 0) - (b.instance_count ?? 0));
      case "created_at":
        return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case "closed_at": {
        const aVal = a.closed_at ? new Date(a.closed_at).getTime() : 0;
        const bVal = b.closed_at ? new Date(b.closed_at).getTime() : 0;
        return dir * (aVal - bVal);
      }
      default:
        return 0;
    }
  });
  return sorted;
}

export function AdminWizards() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  const { data: templates, isLoading } = useQuery({
    queryKey: ["wizardTemplates"],
    queryFn: () => listWizardTemplates(),
    enabled: isAdmin,
  });

  // Filter & sort state
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState<SortState>({ column: "created_at", direction: "desc" });

  // Create wizard dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", slug: "", description: "", is_active: false });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Filtered + sorted data
  const filteredData = useMemo(() => {
    if (!templates) return [];
    let result = templates;

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (tmpl) =>
          tmpl.name.toLowerCase().includes(q) ||
          (tmpl.description?.toLowerCase().includes(q)) ||
          tmpl.slug.toLowerCase().includes(q)
      );
    }

    // Status filter
    const statusFilter = filterValues.status;
    if (statusFilter) {
      const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
      if (statuses.length > 0) {
        result = result.filter((tmpl) => statuses.includes(getStatus(tmpl)));
      }
    }

    return sortTemplates(result, sortState);
  }, [templates, searchQuery, filterValues, sortState]);

  const handleNameChange = (name: string) => {
    setCreateForm((prev) => ({
      ...prev,
      name,
      ...(slugManuallyEdited ? {} : { slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }),
    }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugManuallyEdited(true);
    setCreateForm((prev) => ({ ...prev, slug }));
  };

  const handleCreateSubmit = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const created = await createWizardTemplate({
        name: createForm.name.trim(),
        slug: createForm.slug.trim(),
        description: createForm.description.trim() || undefined,
        is_active: createForm.is_active,
      });
      await queryClient.invalidateQueries({ queryKey: ["wizardTemplates"] });
      setShowCreateDialog(false);
      setCreateForm({ name: "", slug: "", description: "", is_active: false });
      setSlugManuallyEdited(false);
      navigate(adminRoutes.wizard(created.id));
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsCreating(false);
    }
  };

  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setCreateForm({ name: "", slug: "", description: "", is_active: false });
    setSlugManuallyEdited(false);
    setCreateError(null);
  };

  const handleRowClick = useCallback((tmpl: WizardTemplate) => {
    navigate(adminRoutes.wizard(tmpl.id));
  }, [navigate]);

  const handleFilterChange = useCallback((values: FilterValues) => {
    setFilterValues(values);
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t("adminWizards.title")}
        description={t("adminWizards.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminWizards.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminWizards.breadcrumbs.admin") },
          { label: t("adminWizards.breadcrumbs.wizards") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={adminRoutes.wizardHelp}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
              Help
            </Link>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Wizard
            </button>
          </div>
        }
      />

      <KiiList<WizardTemplate>
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        searchPlaceholder="Search by name, slug, or description..."
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearchQueryChange}
        columns={columns}
        data={filteredData}
        isLoading={isLoading}
        emptyMessage="No wizard templates found"
        emptyIcon={<Wand2 className="h-10 w-10 text-muted-foreground" />}
        showCount
        countLabel={(count) => `${count} template${count !== 1 ? "s" : ""}`}
        onRowClick={handleRowClick}
        sortState={sortState}
        onSortChange={setSortState}
        renderRow={(tmpl) => {
          const status = getStatus(tmpl);
          return (
            <TableRow key={tmpl.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-nara-orange/10 flex items-center justify-center shrink-0">
                    <Wand2 className="h-4 w-4 text-nara-orange" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{tmpl.name}</span>
                      {status === "inactive" && (
                        <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded font-medium">
                          inactive
                        </span>
                      )}
                      {status === "closed" && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-medium">
                          closed
                        </span>
                      )}
                      {status === "active" && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded font-medium">
                          active
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">{tmpl.slug}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm max-w-[300px] truncate">
                {tmpl.description || "\u2014"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {tmpl.instance_count ?? "\u2014"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(tmpl.created_at, "date")}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {tmpl.closed_at ? formatDate(tmpl.closed_at, "date") : "\u2014"}
              </TableCell>
            </TableRow>
          );
        }}
      />

      {/* Create Wizard Dialog */}
      <KiiDialogLayout
        open={showCreateDialog}
        onOpenChange={(open) => { if (!open) resetCreateDialog(); }}
        title="Create Wizard"
        description="Create a new wizard template"
        size="md"
        footer={
          <>
            <button
              onClick={resetCreateDialog}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateSubmit}
              disabled={isCreating || !createForm.name.trim() || !createForm.slug.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </button>
          </>
        }
      >
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="My Wizard"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Slug *</label>
            <input
              type="text"
              value={createForm.slug}
              onChange={(e) => handleSlugChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              placeholder="my-wizard"
              className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
            />
            <p className="mt-1 text-xs text-muted-foreground">Used in URLs: /wizard/{createForm.slug || "..."}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={createForm.description}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50 resize-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="create-active"
              checked={createForm.is_active}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 rounded border-border"
            />
            <label htmlFor="create-active" className="text-sm text-foreground">Active</label>
          </div>
          {createError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              {createError}
            </div>
          )}
        </div>
      </KiiDialogLayout>
    </div>
  );
}
